import { getAuthHeaders } from 'services/auth';
import { logError } from 'services/logging';
import { stripRoutePrefix } from 'services/theming';
import { config } from 'config/environments';
import { saveBlob } from './download-file';

/**
 * A market page to download as a PDF: its path and query on the market origin
 * (whitelabel prefix included), and the downloaded file's name without extension.
 */
export type PdfPage = { path: string; name: string };

const DOCS_PDF_PARAMS = [
  'documentID',
  'orderID',
  'quoteID',
  'documentType',
  'paymentType',
  'profileID',
  'shipmentID',
  'issued',
  'transacted',
];
const CASE_TRANSFERS_PARAMS = ['profileID', 'search', 'outstandingOnly', 'start', 'end', 'rows'];
const ACCOUNTING_PARAMS = ['profileID', 'type'];

/** /quality-report/:id/:type, optionally followed by the /:printerFriendly segment. */
const QUALITY_REPORT_PATH = /^\/quality-report\/([^/]+)\/([^/]+)(?:\/[^/]*)?\/?$/;

const pickParams = (from: URLSearchParams, keys: string[]) => {
  const picked = new URLSearchParams();
  keys.forEach((key) => {
    const value = from.get(key);
    if (value !== null) picked.set(key, value);
  });
  return picked;
};

/**
 * Maps a market page to the gapi endpoint that renders it natively, as a path
 * and query on the gapi origin, or null when no endpoint renders that page. The
 * whitelabel prefix is dropped: the endpoints theme by the X-Platform header.
 */
export const nativePdfPath = ({ path, name }: PdfPage): string | null => {
  const queryStart = path.indexOf('?');
  const pathname = stripRoutePrefix(queryStart === -1 ? path : path.slice(0, queryStart)).replace(/\/+$/, '') || '/';
  const query = new URLSearchParams(queryStart === -1 ? '' : path.slice(queryStart + 1));

  let endpoint: string;
  let params: URLSearchParams;
  const qualityReport = pathname.match(QUALITY_REPORT_PATH);
  if (qualityReport) {
    endpoint = '/docs/pdf/quality-report';
    params = new URLSearchParams({
      shipmentID: decodeURIComponent(qualityReport[1]),
      type: decodeURIComponent(qualityReport[2]),
    });
  } else {
    switch (pathname) {
      case '/sourceDoc':
      case '/orderDoc':
      case '/quoteDoc':
        endpoint = '/docs/pdf';
        params = pickParams(query, DOCS_PDF_PARAMS);
        break;
      case '/pallets':
        endpoint = '/docs/pdf/case-transfers';
        params = pickParams(query, CASE_TRANSFERS_PARAMS);
        break;
      case '/accountingDoc':
        endpoint = '/docs/pdf/accounting';
        params = pickParams(query, ACCOUNTING_PARAMS);
        break;
      default:
        return null;
    }
  }
  params.set('name', name);
  return `${endpoint}?${params.toString()}`;
};

/** gapi's origin; config.gapiURL is its /graphql URL. */
const gapiOrigin = () => new URL(config.gapiURL).origin;

/** The page currently shown, with an optional extra path segment (the printer-friendly flag). */
export const currentPdfPage = (name: string, pathSuffix = ''): PdfPage => ({
  path: `${window.location.pathname}${pathSuffix}${window.location.search}`,
  name,
});

/**
 * The page a `method:download?url=<inner path>` action names. The inner URL may be
 * percent-encoded and carries the file name as its own `name` param; the outer
 * `name` and then the fallback are used when it does not.
 */
export const methodDownloadPdfPage = (
  methodDownloadHref: string,
  options?: { outerName?: string | null; fallbackName?: string },
): PdfPage => {
  const match = methodDownloadHref.match(/[?&]url=([^#]*)/);
  let path = match?.[1] ?? '';
  try {
    path = decodeURIComponent(path);
  } catch {
    // Not percent-encoded: use it as given.
  }
  if (path && !path.startsWith('/')) path = `/${path}`;

  const innerParams = new URLSearchParams(path.split('?')[1] || '');
  const name =
    innerParams.get('name')?.trim() || options?.outerName?.trim() || options?.fallbackName || 'downloaded_file';
  return { path, name };
};

/** gapi's mark on an error response whose body is a message for the user. */
const CUSTOMER_MESSAGE_HEADER = 'X-Customer-Message';

/** A download gapi refused with a message for the user, such as an archived order. */
export class PdfCustomerError extends Error {}

export const pdfDownloadErrorMessage = (error: unknown) =>
  error instanceof PdfCustomerError ? error.message : 'Failed to download PDF';

/**
 * Resolves once the PDF is saved or, in the Nile iOS app, handed to the bridge,
 * which then reports downloadComplete/downloadError as a window message.
 */
export const downloadPdf = async (page: PdfPage) => {
  const filename = `${page.name}.pdf`;
  const nativePath = nativePdfPath(page);
  // Callers show the generic error for anything but a PdfCustomerError, so every
  // other failure is reported here.
  if (!nativePath) {
    const error = new Error('No PDF endpoint renders this page');
    logError(error, { extra: { path: page.path } });
    throw error;
  }

  const pdfEndpoint = nativePath.split('?')[0];
  let response: Response;
  try {
    response = await fetch(`${gapiOrigin()}${nativePath}`, { headers: await getAuthHeaders() });
  } catch (error) {
    logError(error, { tags: { pdfEndpoint }, extra: { path: nativePath } });
    throw error;
  }
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    if (response.headers.get(CUSTOMER_MESSAGE_HEADER) && body.trim()) {
      throw new PdfCustomerError(body.trim());
    }
    const error = new Error(`Download failed with status ${response.status}`);
    logError(error, {
      tags: { pdfEndpoint, pdfStatus: response.status },
      extra: { path: nativePath, body: body.slice(0, 500) },
    });
    throw error;
  }
  await saveBlob(await response.blob(), filename);
};
